// client/src/components/ofertas/OfertasPage.js
import React, { Fragment, useState, useMemo, useCallback } from 'react';
import { useOfertas, useMutateOfertas } from '../../hooks/useOfertas';
import OfertaForm from './OfertaForm';
import OfertaRow from './OfertaRow';
import FiltroOfertas from './FiltroOfertas';
import { PlusCircle, Filter as FilterIcon, Loader2, RefreshCcw, XCircle, Gift, Eye, Trash2, AlertTriangle } from 'lucide-react';
import Pagination from '../common/Pagination';
import useAlert from '../../hooks/useAlert';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const OfertasPage = () => {
    const { setAlert } = useAlert();
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState({});
    const [currentOferta, setCurrentOferta] = useState(null);
    const [selectedOferta, setSelectedOferta] = useState(null);
    const ofertasPerPage = 10;

    const queryParams = useMemo(() => ({
        page: currentPage,
        limit: ofertasPerPage,
        ...activeFilters,
        populate: 'itens.itemId,imagens,franquia,account'
    }), [currentPage, activeFilters]);

    const { data: ofertasData, isLoading, refetch, error } = useOfertas(queryParams);
    const { deleteOferta } = useMutateOfertas();
    const isDeleting = deleteOferta.isPending;

    const ofertas = ofertasData?.data || [];
    const totalOfertas = ofertasData?.count || 0;
    const pagination = ofertasData?.pagination || {};

    const handleApplyFilters = useCallback((filters) => {
        setCurrentPage(1);
        setActiveFilters(filters);
    }, []);

    const handleAdicionarClick = useCallback(() => {
        setCurrentOferta(null);
        setIsFormVisible(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setIsFormVisible(false);
        setCurrentOferta(null);
    }, []);

    const handleDelete = useCallback((ofertaId) => {
        if (window.confirm('Tem certeza que deseja excluir esta oferta? Verifique se ela não está sendo usada em pacotes de clientes.')) {
            deleteOferta.mutate(ofertaId, {
                onSuccess: () => setSelectedOferta(null),
                onError: (err) => {
                     console.error("Erro específico ao deletar oferta:", err);
                }
            });
        }
    }, [deleteOferta]);

    const handleSaveSuccess = useCallback(() => {
        handleCloseForm();
        refetch();
    }, [handleCloseForm, refetch]);

    const handleEditClick = useCallback((oferta) => {
        setCurrentOferta(oferta);
        setIsFormVisible(true);
        setSelectedOferta(null);
    }, []);

    if (error) {
        // .error-state não tinha CSS em lugar nenhum do projeto — a página
        // inteira renderizava como texto puro sem nenhum estilo. Trocado
        // pelo mesmo padrão de erro usado em Clientes.js (table-error-state).
        return (
            <div className="page-management-container">
                <div className="table-error-state">
                    <AlertTriangle size={48} />
                    <h2>Erro ao Carregar Ofertas</h2>
                    <p>{error.response?.data?.error || error.message || 'Ocorreu um erro inesperado.'}</p>
                    <button
                        onClick={() => refetch()}
                        className="action-button action-button-primary"
                    >
                        <RefreshCcw size={18} className="icon"/>
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Gift />}
                    title="Gerenciamento de Ofertas"
                    actions={<>
                        {!isFormVisible && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    disabled={isLoading}
                                >
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {isFormVisible && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary" disabled={isLoading}>
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {isFormVisible && (
                    <div className="content-panel">
                        <OfertaForm
                            key={currentOferta?._id || 'new'}
                            onCloseForm={handleCloseForm}
                            onSaveSuccess={handleSaveSuccess}
                            current={currentOferta}
                        />
                    </div>
                )}

                {showFilters && !isFormVisible && (
                    <div className="content-panel">
                        <h3 className="panel-title">Filtrar Ofertas</h3>
                        <FiltroOfertas onApplyFilters={handleApplyFilters} loading={isLoading} />
                    </div>
                )}
            </div>

            {!isFormVisible && (
                <Fragment>
                    <div className="table-container">
                        <div className="table-wrapper">
                            {isLoading && ofertas.length === 0 ? (
                                <div className="table-loading-state">
                                    <Loader2 size={24} className="animate-spin" />
                                    <span>Carregando ofertas...</span>
                                </div>
                            ) : ofertas.length > 0 ? (
                                <table className="styled-table">
                                    <thead>
                                        <tr>
                                            <th>Nome</th>
                                            <th>Tipo</th>
                                            <th>Precificação</th>
                                            <th>Valor</th>
                                            <th>Status</th>
                                            <th className="text-right">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ofertas.map(oferta => (
                                            <OfertaRow
                                                key={oferta._id}
                                                oferta={oferta}
                                                onOpenActions={setSelectedOferta}
                                                loading={isDeleting}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="table-empty-state">
                                     <Gift size={64} className="text-gray-300 mb-4" />
                                     <h3 className="empty-title">Nenhuma oferta encontrada</h3>
                                     <p className="empty-description">
                                         {Object.keys(activeFilters).length > 0
                                             ? 'Tente ajustar os filtros ou limpar a busca.'
                                             : 'Cadastre a sua primeira oferta clicando em "Adicionar Oferta".'
                                         }
                                     </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {totalOfertas > ofertasPerPage && !isLoading && (
                        <Pagination
                            {...pagination}
                            totalItems={totalOfertas}
                            onPageChange={setCurrentPage}
                            currentPage={currentPage}
                        />
                    )}
                </Fragment>
            )}

            <FabButton
                onClick={handleAdicionarClick}
                title="Adicionar Oferta"
                visible={!isFormVisible}
                disabled={isLoading}
                icon={<Gift size={26} className="icon"/>}
            />

            <ActionsDrawer
                open={!!selectedOferta}
                onClose={() => setSelectedOferta(null)}
                title={selectedOferta?.nome || 'Oferta'}
                actions={selectedOferta ? [
                    {
                        key: 'editar',
                        label: 'Visualizar/Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedOferta),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedOferta._id),
                        destructive: true,
                        loading: isDeleting,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default OfertasPage;